import { useState } from "react";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ActionPlan, { ActionPlanProps } from "./ActionPlan";
import ScoreSelectField from "../../fields/ScoreSelectField";
import VariationSelectField from "../../fields/VariationSelectField";
import { ComponentConfig } from "@measured/puck";

type ScoreData = {
    scoreId: number | null;
    variationId: string | null;
    scoreTitle?: string;
};

type ScoreSelection = {
    config?: ScoreData;
};

type ScoreVariationFieldProps = {
    value: ScoreData | undefined;
    onChange: (value: ScoreData) => void;
};

const ScoreVariationField = (props: ScoreVariationFieldProps) => {
    const { value, onChange } = props;
    const theme = useTheme();

    const [selection, setSelection] = useState<ScoreData>(
        value || {
            scoreId: null,
            variationId: null,
        },
    );

    const updateSelection = (newSelection: Partial<ScoreData>) => {
        const updatedSelection = {
            ...selection,
            ...newSelection,
        };
        setSelection(updatedSelection);
        onChange(updatedSelection);
    };

    return (
        <Stack spacing={theme.spacing(2)}>
            <ScoreSelectField
                value={selection.scoreId}
                onChange={(value: number | undefined) => {
                    updateSelection({
                        scoreId: value || null,
                        variationId: null,
                    });
                }}
                field={{ type: "custom", render: () => <></> }}
                name="score.scoreId"
                id="score-field"
            />

            {selection.scoreId ?
                <VariationSelectField
                    scoreId={selection.scoreId}
                    value={selection.variationId}
                    onChange={(value: string | null) => {
                        updateSelection({
                            variationId: value,
                        });
                    }}
                    field={{ type: "custom", render: () => <></> }}
                    name="score.variationId"
                    id="variation-field"
                />
            :   <></>}
        </Stack>
    );
};

export type ActionPlanConfigProps = Omit<ActionPlanProps, "scoreId" | "variationId" | "actionPlanTitleVisible"> & {
    actionPlanTitleVisible?: string;
    scoreConfig?: ScoreSelection;
    goalSettings?: {
        customGoalValue?: string;
        customGoalYear?: number;
        customGoalUnit?: string;
    };
    displayOptions?: {
        showScoreSection?: string;
        showTrajectorySection?: string;
        showGoalSection?: string;
        showLegend?: string;
    };
};

export const ActionPlanBlock: ComponentConfig<ActionPlanConfigProps> = {
    label: "Action Plan Overview",
    defaultProps: {
        actionPlanTitleVisible: "true",
        scoreConfig: {
            config: {
                scoreId: null,
                variationId: null,
            },
        },
        goalSettings: {
            customGoalValue: "",
            customGoalYear: undefined,
        },
        displayOptions: {
            showScoreSection: "true",
            showTrajectorySection: "true",
            showGoalSection: "true",
            showLegend: "true",
        },
    },
    fields: {
        actionPlanTitleVisible: {
            type: "select",
            label: "Show Action Plan Title",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
        scoreConfig: {
            type: "object",
            label: "Score and variations",
            objectFields: {
                config: {
                    type: "custom",
                    render: (props: ScoreVariationFieldProps) => (
                        <ScoreVariationField value={props.value} onChange={props.onChange} />
                    ),
                },
            },
        },
        goalSettings: {
            type: "object",
            label: "Sector goal settings",
            objectFields: {
                customGoalValue: {
                    type: "text",
                    label: "Sector Goal Value",
                },
                customGoalYear: {
                    type: "number",
                    label: "Sector Goal Year",
                    min: 0,
                },
                customGoalUnit: {
                    type: "text",
                    label: "Sector Goal Unit",
                },
            },
        },
        displayOptions: {
            type: "object",
            label: "Display options",
            objectFields: {
                showScoreSection: {
                    type: "select",
                    label: "Show Diagnostic Section",
                    options: [
                        { label: "Yes", value: "true" },
                        { label: "No", value: "false" },
                    ],
                },
                showTrajectorySection: {
                    type: "select",
                    label: "Show Trajectory Section",
                    options: [
                        { label: "Yes", value: "true" },
                        { label: "No", value: "false" },
                    ],
                },
                showGoalSection: {
                    type: "select",
                    label: "Show Goal Section",
                    options: [
                        { label: "Yes", value: "true" },
                        { label: "No", value: "false" },
                    ],
                },
                showLegend: {
                    type: "select",
                    label: "Show Legend",
                    options: [
                        { label: "Yes", value: "true" },
                        { label: "No", value: "false" },
                    ],
                },
            },
        },
    },
    render: (props: ActionPlanConfigProps) => {
        const { scoreConfig, goalSettings, displayOptions, actionPlanTitleVisible, ...options } = props;
        const scoreData = scoreConfig?.config || { scoreId: null, variationId: null };
        const scoreId = scoreData.scoreId || null;

        let variationId: number | null = null;
        if (scoreData.variationId) {
            const parsed = Number(scoreData.variationId);
            variationId = isNaN(parsed) ? null : parsed;
        }

        let customGoalValue: number | undefined;
        if (goalSettings?.customGoalValue !== undefined && goalSettings?.customGoalValue !== null) {
            const valueAsString =
                typeof goalSettings.customGoalValue === "string" ?
                    goalSettings.customGoalValue
                :   String(goalSettings.customGoalValue);

            const numericValue = valueAsString.replace(/[^0-9.]/g, "");
            customGoalValue = numericValue ? Number(numericValue) : undefined;
        }
        const customGoalYear = goalSettings?.customGoalYear;
        const customGoalUnit = goalSettings?.customGoalUnit;

        const showScoreSection = displayOptions?.showScoreSection !== "false";
        const showTrajectorySection = displayOptions?.showTrajectorySection !== "false";
        const showGoalSection = displayOptions?.showGoalSection !== "false";
        const showLegend = displayOptions?.showLegend !== "false";
        const actionPlanTitleVisibleValue = actionPlanTitleVisible !== "false";

        return (
            <ActionPlan
                scoreId={scoreId}
                variationId={variationId}
                customGoalValue={customGoalValue}
                customGoalYear={customGoalYear}
                customGoalUnit={customGoalUnit}
                showScoreSection={showScoreSection}
                showTrajectorySection={showTrajectorySection}
                showGoalSection={showGoalSection}
                showLegend={showLegend}
                actionPlanTitleVisible={actionPlanTitleVisibleValue}
                {...options}
            />
        );
    },
};

export default ActionPlanBlock;
